import React, { useEffect } from 'react';
import { X, ExternalLink, AlertCircle, Play } from 'lucide-react';

const TrailerModal = ({ trailer, isOpen, onClose, movieTitle }) => {
    // Modal dışına tıklandığında kapatma
    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    // Escape tuşu ile kapatma
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, onClose]);

    // Body scroll'unu engelle/geri aç
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    // Fragman yok durumu
    if (!trailer) {
        return (
            <div
                className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4"
                onClick={handleOverlayClick}
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 999999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full p-6 shadow-2xl">
                    {/* Close Button */}
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 z-10 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-all"
                    >
                        <X className="h-5 w-5" />
                    </button>

                    {/* Error Content */}
                    <div className="text-center py-4">
                        <AlertCircle className="h-16 w-16 text-orange-500 mx-auto mb-4" />
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                            Fragman Bulunamadı
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400 mb-4">
                            <span className="font-medium">{movieTitle}</span> filmi için maalesef fragman bulunamadı.
                        </p>
                        <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-4">
                            <p className="text-sm text-blue-800 dark:text-blue-300">
                                💡 <strong>İpucu:</strong> Youtube'da "<em>{movieTitle} trailer</em>" aratarak fragmanı bulabilirsiniz.
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                        >
                            Tamam
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-90 p-4"
            onClick={handleOverlayClick}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 999999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}
        >
            <div className="bg-black rounded-2xl max-w-5xl w-full shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="bg-gray-900 px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        <Play className="h-6 w-6 text-red-500" />
                        <div>
                            <h3 className="text-white font-bold text-lg">
                                {trailer.movieTitle} {trailer.movieYear && `(${trailer.movieYear})`}
                            </h3>
                            <p className="text-gray-400 text-sm">
                                {trailer.name} • {trailer.type}
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center space-x-2">
                        {/* YouTube'da Aç */}
                        <a
                            href={trailer.watchUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center space-x-2 px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-sm"
                        >
                            <ExternalLink className="h-4 w-4" />
                            <span>YouTube'da Aç</span>
                        </a>

                        {/* Kapat */}
                        <button
                            onClick={onClose}
                            className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                </div>

                {/* Video Container */}
                <div className="relative bg-black" style={{ paddingBottom: '56.25%' /* 16:9 Aspect Ratio */ }}>
                    <iframe
                        src={trailer.url}
                        title={`${trailer.movieTitle} - ${trailer.name}`}
                        className="absolute inset-0 w-full h-full"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                    />
                </div>

                {/* Footer */}
                <div className="bg-gray-900 px-6 py-3 text-center">
                    <p className="text-gray-400 text-xs">
                        🎬 TMDB API ile desteklenmektedir • YouTube fragmanı
                    </p>
                </div>
            </div>
        </div>
    );
};

export default TrailerModal; 